import { useMemo } from "react";

import useUsers from "@/hooks/useUsers";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useCurrentEnvironment from "@/hooks/use-environment";

/**
 * This hook generates a URL to Freshpaint's Zendesk support form, along with
 * pre-filled form fields containing helpful debugging information.
 *
 * The goal of this hook is to make it easier for our support teams to quickly
 * identify the proper account and environment when a user submits a support
 * request, and to include a bit of context about the user's browser and the
 * page they were on when they decided to reach out for help.
 */
export default function useSupportFormURL(): URL {
  const { loading: userLoading, currentUser } = useUsers();
  const accountMetadata = useAccountMetadata();
  const currentEnvironment = useCurrentEnvironment();

  return useMemo(() => {
    if (
      userLoading ||
      !currentUser ||
      !accountMetadata ||
      !accountMetadata.loaded
    ) {
      return new URL("https://support.freshpaint.io/hc/en-us/requests/new");
    }

    const accountDetails = [
      `<strong>Account:</strong> ${accountMetadata?.companyDetails?.name || "(unnamed account)"} (${accountMetadata?.accountID || "(unknown)"})`,
      `<strong>Project:</strong> ${currentEnvironment?.project?.name || "(unknown)"} (${currentEnvironment?.project.ID})`,
      `<strong>Environment:</strong> ${currentEnvironment?.name || "(unknown)"} (${currentEnvironment?.ID || "(unknown)"})`,
      `<strong>User:</strong> ${currentUser.email} (${currentUser.uid})`,
    ];

    const pageDetails = [
      `<strong>Page URL:</strong> ${window.location.href}`,
      `<strong>User Agent:</strong> ${navigator.userAgent}`,
      `<strong>Referrer:</strong> ${document.referrer || "(none)"}`,
    ];

    const email = currentUser.email || "";

    const description = `
Please describe your issue here:<br /><br /><br /><br /><br />
<em>The debugging information below will help the Freshpaint support team more quickly resolve your issue:</em>
<h4>Account Details</h4>
\t${accountDetails.join("\t<br />")}
<h4>Page Details</h4>
\t${pageDetails.join("\t<br />")}
`;

    /**
     * These query parameters belong to Zendesk's support form page. They allow
     * pre-filling certain fields in the support request form.
     *
     * @see https://support.zendesk.com/hc/en-us/articles/4408839114522-Creating-pre-filled-ticket-forms
     * @see https://support.zendesk.com/hc/en-us/articles/4408886739098-About-ticket-fields
     */
    const queryParams = new URLSearchParams({
      tf_anonymous_requester_email: email,
      tf_description: description,
    });

    return new URL(
      `https://support.freshpaint.io/hc/en-us/requests/new?${queryParams.toString()}`,
    );
  }, [userLoading, currentUser, accountMetadata, currentEnvironment]);
}
